import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { getPersonale, createPersonale, updatePersonale, deletePersonale } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users } from 'lucide-react';

const PersonaleForm = ({ persona, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    nome: persona?.nome || '',
    ruolo: persona?.ruolo || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome) {
      toast.error('Il nome è obbligatorio');
      return;
    }
    setLoading(true);
    try {
      await onSubmit(formData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="nome">Nome *</Label>
        <Input
          id="nome"
          value={formData.nome}
          onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
          placeholder="Es: Mario Rossi"
          className="h-12"
          data-testid="personale-nome"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ruolo">Ruolo (opzionale)</Label>
        <Input
          id="ruolo"
          value={formData.ruolo}
          onChange={(e) => setFormData({ ...formData, ruolo: e.target.value })}
          placeholder="Es: Barista, Cameriere..."
          className="h-12"
          data-testid="personale-ruolo"
        />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button type="submit" disabled={loading} data-testid="personale-submit">
          {loading ? 'Salvataggio...' : persona ? 'Aggiorna' : 'Aggiungi'}
        </Button>
      </div>
    </form>
  );
};

const Personale = () => {
  const [personale, setPersonale] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPersona, setEditingPersona] = useState(null);

  useEffect(() => {
    loadPersonale();
  }, []);

  const loadPersonale = async () => {
    try {
      const data = await getPersonale();
      setPersonale(data);
    } catch (error) {
      toast.error('Errore nel caricamento personale');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      await createPersonale(data);
      toast.success('Personale aggiunto!');
      setDialogOpen(false);
      loadPersonale();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleUpdate = async (data) => {
    try {
      await updatePersonale(editingPersona.id, data);
      toast.success('Personale aggiornato!');
      setDialogOpen(false);
      setEditingPersona(null);
      loadPersonale();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa persona?')) return;
    try {
      await deletePersonale(id);
      toast.success('Personale eliminato!');
      loadPersonale();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const openEdit = (persona) => {
    setEditingPersona(persona);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingPersona(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="personale-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Personale</h1>
          <p className="text-muted-foreground mt-1">Gestisci il personale per le spese</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditingPersona(null)} data-testid="add-personale-btn">
              <Plus className="h-4 w-4 mr-2" />
              Aggiungi
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>
                {editingPersona ? 'Modifica Personale' : 'Nuovo Personale'}
              </DialogTitle>
              <DialogDescription>
                {editingPersona ? 'Modifica i dati' : 'Aggiungi una persona per le spese'}
              </DialogDescription>
            </DialogHeader>
            <PersonaleForm
              persona={editingPersona}
              onSubmit={editingPersona ? handleUpdate : handleCreate}
              onCancel={closeDialog}
            />
          </DialogContent>
        </Dialog>
      </div>

      {personale.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nessun personale registrato</p>
            <p className="text-sm text-muted-foreground">Aggiungi il personale per tracciare le spese</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Ruolo</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {personale.map((persona) => (
                    <TableRow key={persona.id} data-testid={`personale-row-${persona.id}`}>
                      <TableCell className="font-medium">{persona.nome}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {persona.ruolo || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(persona)}
                            data-testid={`edit-personale-${persona.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(persona.id)}
                            data-testid={`delete-personale-${persona.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Personale;
